import { useEffect, useRef, useState, useCallback, useMemo } from "react";
import ActionButton from "@/components/ActionButton";
import { useSearchParams, useNavigate } from "react-router-dom";
import api, { formatDate, API } from "@/lib/api";
import { Plus, Pencil, Trash2, ExternalLink, Upload, Loader2, X, FileText, FileDown, Mail, Sparkles } from "lucide-react";

const COLS = [
  { id: "a_creer", label: "À créer" },
  { id: "en_preparation", label: "En préparation" },
  { id: "depose", label: "Déposé" },
  { id: "complement_demande", label: "Complément demandé" },
  { id: "accorde", label: "Accordé" },
  { id: "refuse", label: "Refusé" },
];

const TYPES = ["AT_ERP", "AT_CENTRE_CO", "DP", "ENSEIGNE", "PC"];
const TYPE_LABEL = {
  AT_ERP: "AT-ERP (4 mois)", AT_CENTRE_CO: "AT-CENTRE CO (4 mois)",
  DP: "DP (1 mois)", ENSEIGNE: "ENSEIGNE (1 mois)", PC: "PC (2 mois)",
  // libellés legacy (démarches existantes)
  CENTRE_COMMERCIAL: "Centre commercial (4 mois)", ERP: "ERP (4 mois)", VIDEOPROTECTION: "Vidéoprotection",
};

// ---- Calcul du délai d'instruction (miroir du backend) ----
export const DEMARCHE_DELAI = { AT_ERP: 4, AT_CENTRE_CO: 4, ERP: 4, CENTRE_COMMERCIAL: 4, DP: 1, ENSEIGNE: 1, PC: 2 };
export function demarcheDelaiMois(type, abf, override) {
  if (override) { const n = parseInt(override); if (!isNaN(n)) return n; }
  if (type === "DP" || type === "ENSEIGNE") return abf ? 2 : 1;
  if (type === "PC") return abf ? 3 : 2;
  return DEMARCHE_DELAI[type] || 2;
}
export function addMonthsISO(iso, n) {
  if (!iso) return "";
  const d = new Date(iso.slice(0, 10) + "T00:00:00");
  const day = d.getDate();
  d.setDate(1); d.setMonth(d.getMonth() + n);
  const last = new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
  d.setDate(Math.min(day, last));
  return d.toISOString().slice(0, 10);
}
export function computeLimite(type, depot, abf, override) {
  if (!depot) return "";
  return addMonthsISO(depot, demarcheDelaiMois(type, abf, override));
}
const INTERLOCUTEUR_ROLES = ["Mairie", "Responsable sécurité", "Responsable accessibilité", "Bureau de contrôle", "Autre"];

// Suivi dossier mairie (note d'avancement)
const MAIRIE_STATUS = [
  { id: "", label: "— Non renseigné —" },
  { id: "depose", label: "Déposé" },
  { id: "valide", label: "Validé" },
  { id: "refus", label: "Refus" },
  { id: "consultation_abf", label: "Consultation ABF" },
];
const MAIRIE_LABEL = Object.fromEntries(MAIRIE_STATUS.map((m) => [m.id, m.label]));
const MAIRIE_COLOR = {
  depose: "bg-blue-50 text-blue-700",
  valide: "bg-emerald-50 text-emerald-700",
  refus: "bg-rose-50 text-rose-700",
  consultation_abf: "bg-amber-50 text-amber-700",
};

const BLANK_DEMARCHE = { type: "AT_ERP", type_custom: "", project_id: "", reference: "", date_depot: "", date_limite: "", zone_protegee: false, instructeur_nom: "", instructeur_email: "", plateforme: "", identifiant_mail: "", identifiant_mdp: "", commentaire: "" };

export default function Demarches() {
  const [list, setList] = useState([]);
  const [projects, setProjects] = useState([]);
  const [show, setShow] = useState(false);
  const [createForm, setCreateForm] = useState(BLANK_DEMARCHE);
  const [editing, setEditing] = useState(null);
  const [dragId, setDragId] = useState(null);
  const [dragOverCol, setDragOverCol] = useState(null);

  const grouped = useMemo(() => {
    const g = {};
    for (const d of list) (g[d.status] ||= []).push(d);
    return g;
  }, [list]);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const colRefs = useRef({});

  const load = async () => {
    const [d, p] = await Promise.all([api.get("/demarches"), api.get("/projects")]);
    setList(d.data); setProjects(p.data);
  };
  useEffect(() => { load(); }, []);

  useEffect(() => {
    const status = searchParams.get("status");
    const focus = searchParams.get("focus");
    if (status && colRefs.current[status]) {
      colRefs.current[status].scrollIntoView({ behavior: "smooth", inline: "center" });
      colRefs.current[status].classList.add("ring-2", "ring-[var(--ppd)]");
      setTimeout(() => colRefs.current[status]?.classList.remove("ring-2", "ring-[var(--ppd)]"), 2500);
    }
    if (focus) {
      const el = document.querySelector(`[data-demarche-id="${focus}"]`);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        el.classList.add("ring-2", "ring-[var(--ppd)]");
        setTimeout(() => el.classList.remove("ring-2", "ring-[var(--ppd)]"), 2500);
      }
    }
  }, [searchParams, list]);

  const setStatus = async (id, status) => {
    await api.put(`/demarches/${id}`, { status });
    load();
  };

  const handleDrop = async (status) => {
    const id = dragId;
    setDragId(null); setDragOverCol(null);
    if (!id) return;
    const d = list.find((x) => x.id === id);
    if (!d || d.status === status) return;
    setList((prev) => prev.map((x) => (x.id === id ? { ...x, status } : x)));
    try { await api.put(`/demarches/${id}`, { status }); } finally { load(); }
  };

  const del = async (id) => {
    if (!window.confirm("Supprimer cette démarche ?")) return;
    await api.delete(`/demarches/${id}`);
    load();
  };

  const create = async (e) => {
    e?.preventDefault?.();
    const proj = projects.find((p) => p.id === createForm.project_id);
    const type = createForm.type === "__autre__" ? (createForm.type_custom || "").trim() : createForm.type;
    if (!type) return;
    const payload = {
      type,
      project_id: createForm.project_id || null,
      project_nom: proj ? proj.nom : "",
      reference: createForm.reference || "",
      date_depot: createForm.date_depot || "",
      date_limite: createForm.date_limite || "",
      date_limite_auto: true,
      zone_protegee: !!createForm.zone_protegee,
      instructeur_nom: createForm.instructeur_nom || "",
      instructeur_email: createForm.instructeur_email || "",
      plateforme: createForm.plateforme || "",
      identifiant_mail: createForm.identifiant_mail || "",
      identifiant_mdp: createForm.identifiant_mdp || "",
      commentaire: createForm.commentaire || "",
    };
    await api.post("/demarches", payload);
    setShow(false);
    setCreateForm(BLANK_DEMARCHE);
    load();
  };

  const openProject = (d) => {
    if (d.project_id) navigate(`/projets/${d.project_id}`);
  };

  return (
    <div className="space-y-6" data-testid="demarches-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-gray-500">Administratif</div>
          <h1 className="font-display text-4xl font-bold tracking-tighter mt-1">Démarches</h1>
        </div>
        <button data-testid="demarche-new-button" onClick={() => setShow(true)} className="bg-gray-900 text-white px-4 py-2 rounded text-sm font-semibold flex items-center gap-2"><Plus size={16}/> Nouvelle démarche</button>
      </div>

      <div className="flex gap-4 overflow-x-auto pb-4 kanban-scroll">
        {COLS.map((c) => {
          const items = grouped[c.id] || [];
          return (
            <div ref={(el) => { colRefs.current[c.id] = el; }} key={c.id}
              onDragOver={(e) => { e.preventDefault(); if (dragOverCol !== c.id) setDragOverCol(c.id); }}
              onDragLeave={(e) => { if (e.currentTarget === e.target) setDragOverCol(null); }}
              onDrop={() => handleDrop(c.id)}
              className={`min-w-[260px] flex-1 rounded-lg p-3 border transition ${dragOverCol === c.id ? "bg-[var(--ppd)]/5 border-[var(--ppd)]/40 ring-2 ring-[var(--ppd)]/30" : "bg-stone-50/60 border-stone-100"}`} data-testid={`demarche-col-${c.id}`}>
              <div className="flex justify-between items-center mb-3">
                <div className="text-xs font-bold uppercase tracking-widest text-stone-600">{c.label}</div>
                <div className="text-xs font-semibold text-stone-400">{items.length}</div>
              </div>
              <div className="space-y-2">
                {items.map((d) => (
                  <div key={d.id} data-demarche-id={d.id}
                    draggable
                    onDragStart={(e) => { setDragId(d.id); e.dataTransfer.effectAllowed = "move"; }}
                    onDragEnd={() => { setDragId(null); setDragOverCol(null); }}
                    className={`card-flat p-3 transition group cursor-grab active:cursor-grabbing ${dragId === d.id ? "opacity-40" : ""}`} data-testid={`demarche-${d.id}`}>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="pill bg-stone-100 text-stone-700">{d.type}</span>
                      {d.reference && <span className="text-[10px] font-mono text-stone-500">{d.reference}</span>}
                      <div className="ml-auto flex items-center gap-1 opacity-60 group-hover:opacity-100">
                        <button onClick={() => setEditing(d)} data-testid={`demarche-edit-${d.id}`} className="text-stone-400 hover:text-[#C26B53]" title="Modifier"><Pencil size={13}/></button>
                        <button onClick={() => del(d.id)} data-testid={`demarche-del-${d.id}`} className="text-stone-400 hover:text-rose-600" title="Supprimer"><Trash2 size={13}/></button>
                      </div>
                    </div>
                    <button
                      onClick={() => openProject(d)}
                      data-testid={`demarche-open-project-${d.id}`}
                      disabled={!d.project_id}
                      className={`text-sm font-semibold text-stone-900 truncate text-left w-full ${d.project_id ? "hover:text-[var(--ppd)] cursor-pointer" : "cursor-default"}`}
                      title={d.project_id ? "Ouvrir la fiche projet" : "Aucun projet rattaché"}
                    >
                      {d.project_nom || "Sans projet"} {d.project_id && <ExternalLink size={11} className="inline -mt-0.5 text-stone-400"/>}
                    </button>
                    <div className="text-xs text-stone-500">Limite: {formatDate(d.date_limite)}</div>
                    <div className="mt-1.5 flex items-center gap-1.5 flex-wrap">
                      {d.mairie_status && <span className={`pill text-[10px] ${MAIRIE_COLOR[d.mairie_status] || "bg-stone-100 text-stone-600"}`}>Mairie : {MAIRIE_LABEL[d.mairie_status]}</span>}
                      {(d.documents || []).length > 0 && <span className="pill bg-stone-100 text-stone-600 text-[10px] flex items-center gap-0.5"><FileText size={9}/> {d.documents.length}</span>}
                    </div>
                    <div className="mt-2 flex gap-1 flex-wrap">
                      {COLS.filter((x) => x.id !== d.status).slice(0, 3).map((x) => (
                        <button key={x.id} onClick={() => setStatus(d.id, x.id)} className="text-[10px] px-2 py-0.5 border border-stone-200 rounded text-stone-600 hover:bg-stone-100">→ {x.label}</button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {/* CREATE */}
      {show && (() => {
        const cf = createForm;
        const setCf = (k, v) => setCreateForm((p) => ({ ...p, [k]: v }));
        const effType = cf.type === "__autre__" ? "" : cf.type;
        const limite = computeLimite(effType, cf.date_depot, cf.zone_protegee, null);
        const abfApplies = ["DP", "ENSEIGNE", "PC"].includes(effType);
        return (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-start sm:items-center justify-center p-4 overflow-y-auto">
          <form onSubmit={create} className="bg-white rounded-lg p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto my-auto" data-testid="demarche-form">
            <h3 className="font-display text-2xl font-bold mb-4">Nouvelle démarche</h3>
            <div className="space-y-3 text-sm">
              <select value={cf.type} onChange={(e) => setCf("type", e.target.value)} data-testid="dc-type" className="w-full border border-gray-300 rounded px-3 py-2">
                {TYPES.map((t) => <option key={t} value={t}>{TYPE_LABEL[t] || t}</option>)}
                <option value="__autre__">Autre démarche…</option>
              </select>
              {cf.type === "__autre__" && (
                <input value={cf.type_custom} onChange={(e) => setCf("type_custom", e.target.value)} placeholder="Intitulé de la démarche" data-testid="dc-type-custom" className="w-full border border-gray-300 rounded px-3 py-2"/>
              )}
              {abfApplies && (
                <label className="flex items-center gap-2 text-xs font-semibold text-gray-700 bg-stone-50 border border-stone-100 rounded p-2.5">
                  <input type="checkbox" checked={cf.zone_protegee} onChange={(e) => setCf("zone_protegee", e.target.checked)} data-testid="dc-abf" className="accent-[#C26B53]"/>
                  ABF (site protégé) {effType === "PC" ? "→ +1 mois" : "→ 2 mois"}
                </label>
              )}
              <select value={cf.project_id} onChange={(e) => setCf("project_id", e.target.value)} className="w-full border border-gray-300 rounded px-3 py-2">
                <option value="">— Projet (optionnel) —</option>
                {projects.map((p) => <option key={p.id} value={p.id}>{p.nom}</option>)}
              </select>
              <input value={cf.reference} onChange={(e) => setCf("reference", e.target.value)} placeholder="Référence dossier" className="w-full border border-gray-300 rounded px-3 py-2"/>
              <div className="grid grid-cols-2 gap-2">
                <label className="flex flex-col"><span className="text-[10px] uppercase tracking-widest text-gray-500 font-bold mb-1">Date de dépôt</span>
                  <input type="date" value={cf.date_depot} onChange={(e) => setCf("date_depot", e.target.value)} data-testid="dc-depot" className="border border-gray-300 rounded px-3 py-2"/></label>
                <label className="flex flex-col"><span className="text-[10px] uppercase tracking-widest text-gray-500 font-bold mb-1">Échéance (auto)</span>
                  <input type="date" value={limite} readOnly data-testid="dc-limite" className="border border-gray-300 rounded px-3 py-2 bg-gray-100 text-gray-500"/></label>
              </div>
              <input value={cf.instructeur_nom} onChange={(e) => setCf("instructeur_nom", e.target.value)} placeholder="Instructeur" className="w-full border border-gray-300 rounded px-3 py-2"/>
              <input value={cf.instructeur_email} onChange={(e) => setCf("instructeur_email", e.target.value)} placeholder="Email instructeur" className="w-full border border-gray-300 rounded px-3 py-2"/>
              <input value={cf.plateforme} onChange={(e) => setCf("plateforme", e.target.value)} placeholder="Plateforme numérique (URL)" className="w-full border border-gray-300 rounded px-3 py-2"/>
              <div className="border border-stone-100 bg-stone-50 rounded p-2.5 space-y-2">
                <div className="text-[10px] uppercase tracking-widest text-gray-500 font-bold">Identifiants</div>
                <input value={cf.identifiant_mail} onChange={(e) => setCf("identifiant_mail", e.target.value)} placeholder="Adresse mail" data-testid="dc-id-mail" className="w-full border border-gray-300 rounded px-3 py-2"/>
                <input value={cf.identifiant_mdp} onChange={(e) => setCf("identifiant_mdp", e.target.value)} placeholder="Mot de passe" data-testid="dc-id-mdp" className="w-full border border-gray-300 rounded px-3 py-2"/>
              </div>
              <textarea value={cf.commentaire} onChange={(e) => setCf("commentaire", e.target.value)} placeholder="Commentaires (suivi)…" rows={2} data-testid="dc-commentaire" className="w-full border border-gray-300 rounded px-3 py-2"/>
            </div>
            <div className="flex gap-2 mt-5 justify-end">
              <button type="button" onClick={() => setShow(false)} className="px-4 py-2 text-sm border rounded">Annuler</button>
              <ActionButton type="button" onClick={create} data-testid="demarche-submit" successLabel="Créé" className="px-4 py-2 text-sm bg-gray-900 text-white rounded font-semibold">Créer</ActionButton>
            </div>
          </form>
        </div>
        );
      })()}

      {/* EDIT */}
      {editing && (
        <DemarcheEditModal
          demarche={editing}
          projects={projects}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

export function DemarcheEditModal({ demarche, projects, onClose, onSaved }) {
  const [d, setD] = useState({ ...demarche });
  const [docs, setDocs] = useState(demarche.documents || []);
  const [etapes, setEtapes] = useState(demarche.etapes || []);
  const [interlocs, setInterlocs] = useState(demarche.interlocuteurs || []);
  const [uploading, setUploading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [kind, setKind] = useState("depose");
  const [newEtape, setNewEtape] = useState({ date: new Date().toISOString().slice(0, 10), titre: "", commentaire: "" });
  const set = (k, v) => setD((p) => ({ ...p, [k]: v }));

  const save = async () => {
    const proj = projects.find((p) => p.id === d.project_id);
    const payload = {
      type: d.type, project_id: d.project_id || null, project_nom: proj ? proj.nom : (d.project_id ? d.project_nom : ""),
      reference: d.reference || "", date_depot: d.date_depot || "", date_limite: d.date_limite || "", date_recepisse: d.date_recepisse || "",
      date_limite_auto: d.date_limite_auto !== false, delai_mois_override: d.delai_mois_override || null, zone_protegee: !!d.zone_protegee,
      status: d.status, instructeur_nom: d.instructeur_nom || "", instructeur_email: d.instructeur_email || "", instructeur_tel: d.instructeur_tel || "",
      plateforme: d.plateforme || "", identifiants: d.identifiants || "",
      identifiant_mail: d.identifiant_mail || "", identifiant_mdp: d.identifiant_mdp || "",
      mairie_status: d.mairie_status || "", mairie_remarques: d.mairie_remarques || "", notes: d.notes || "",
      mairie_destinataire: d.mairie_destinataire || "", lettre_texte: d.lettre_texte || "",
      commentaire: d.commentaire || "", interlocuteurs: interlocs,
    };
    await api.put(`/demarches/${demarche.id}`, payload);
    onSaved();
  };

  const [genLettre, setGenLettre] = useState(false);
  const [dlLettre, setDlLettre] = useState(false);
  const generateLettre = async () => {
    setGenLettre(true);
    try {
      const { data } = await api.post(`/demarches/${demarche.id}/lettre/generate`);
      setD((p) => ({ ...p, lettre_texte: data.corps || "" }));
    } catch (e) {
      alert("Échec génération lettre : " + (e.response?.data?.detail || e.message));
    } finally { setGenLettre(false); }
  };
  const downloadLettrePdf = async () => {
    setDlLettre(true);
    try {
      const res = await api.post(`/demarches/${demarche.id}/lettre/pdf`,
        { corps: d.lettre_texte || "", destinataire: d.mairie_destinataire || "" },
        { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = `lettre_${d.type}_${(d.project_nom || "projet").replace(/\s+/g, "_")}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      alert("Échec PDF lettre : " + (e.response?.data?.detail || e.message));
    } finally { setDlLettre(false); }
  };

  const exportPdf = async () => {
    setExporting(true);
    try {
      const res = await api.get(`/demarches/${demarche.id}/export-pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `suivi_demarche_${d.type}_${d.reference || demarche.id.slice(0, 6)}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      alert("Échec export PDF : " + (e.response?.data?.detail || e.message));
    } finally { setExporting(false); }
  };

  const addInterloc = () => setInterlocs((p) => [...p, { id: Math.random().toString(36).slice(2), role: "Mairie", nom: "", email: "", tel: "" }]);
  const setInterloc = (id, k, v) => setInterlocs((p) => p.map((it) => it.id === id ? { ...it, [k]: v } : it));
  const delInterloc = (id) => setInterlocs((p) => p.filter((it) => it.id !== id));

  const addEtape = async () => {
    if (!newEtape.titre.trim()) { alert("Donne un titre à l'étape."); return; }
    const { data } = await api.post(`/demarches/${demarche.id}/etapes`, newEtape);
    setEtapes(data.etapes || []);
    setNewEtape({ date: new Date().toISOString().slice(0, 10), titre: "", commentaire: "" });
  };
  const delEtape = async (eid) => {
    const { data } = await api.delete(`/demarches/${demarche.id}/etapes/${eid}`);
    setEtapes(data.etapes || []);
  };

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("kind", kind);
      const { data } = await api.post(`/demarches/${demarche.id}/documents`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setDocs(data.documents || []);
      setEtapes(data.etapes || []);
    } catch (e) {
      alert("Échec de l'import : " + (e.response?.data?.detail || e.message));
    } finally { setUploading(false); }
  };
  const delDoc = async (docId) => {
    const { data } = await api.delete(`/demarches/${demarche.id}/documents/${docId}`);
    setDocs(data.documents || []);
  };

  const KIND_LABEL = { depose: "Dossier déposé", retour: "Retour mairie", autre: "Autre" };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-lg p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="demarche-edit-modal">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display text-2xl font-bold">Modifier la démarche</h3>
          <div className="flex items-center gap-2">
            <button onClick={exportPdf} disabled={exporting} data-testid="de-export-pdf" className="flex items-center gap-1.5 text-xs font-semibold border border-[#C26B53] text-[#C26B53] hover:bg-[#C26B53] hover:text-white px-3 py-1.5 rounded transition-colors disabled:opacity-50">
              {exporting ? <Loader2 size={13} className="animate-spin"/> : <FileDown size={13}/>} Export PDF client
            </button>
            <button onClick={onClose}><X size={18}/></button>
          </div>
        </div>
        <div className="space-y-3 text-sm">
          <div className="grid grid-cols-2 gap-2">
            <label className="flex flex-col"><span className="text-[10px] uppercase tracking-widest text-gray-500 font-bold mb-1">Type</span>
              <select value={TYPES.includes(d.type) ? d.type : "__cur__"} onChange={(e) => set("type", e.target.value)} data-testid="de-type" className="border border-gray-300 rounded px-3 py-2">{TYPES.map((t) => <option key={t} value={t}>{TYPE_LABEL[t] || t}</option>)}{!TYPES.includes(d.type) && <option value="__cur__">{TYPE_LABEL[d.type] || d.type}</option>}</select>
            </label>
            <label className="flex flex-col"><span className="text-[10px] uppercase tracking-widest text-gray-500 font-bold mb-1">Statut</span>
              <select value={d.status} onChange={(e) => set("status", e.target.value)} data-testid="de-status" className="border border-gray-300 rounded px-3 py-2">{COLS.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}</select>
            </label>
          </div>
          <label className="flex flex-col"><span className="text-[10px] uppercase tracking-widest text-gray-500 font-bold mb-1">Projet rattaché</span>
            <select value={d.project_id || ""} onChange={(e) => set("project_id", e.target.value)} data-testid="de-project" className="border border-gray-300 rounded px-3 py-2">
              <option value="">— Aucun —</option>
              {projects.map((p) => <option key={p.id} value={p.id}>{p.nom}</option>)}
            </select>
          </label>
          <input value={d.reference || ""} onChange={(e) => set("reference", e.target.value)} placeholder="Référence dossier" data-testid="de-reference" className="w-full border border-gray-300 rounded px-3 py-2"/>
          <div className="grid grid-cols-3 gap-2">
            <label className="flex flex-col"><span className="text-[10px] uppercase tracking-widest text-gray-500 font-bold mb-1">Dépôt</span><input type="date" value={(d.date_depot || "").slice(0, 10)} onChange={(e) => set("date_depot", e.target.value)} data-testid="de-date-depot" className="border border-gray-300 rounded px-2 py-2"/></label>
            <label className="flex flex-col"><span className="text-[10px] uppercase tracking-widest text-gray-500 font-bold mb-1">Récépissé</span><input type="date" value={(d.date_recepisse || "").slice(0, 10)} onChange={(e) => set("date_recepisse", e.target.value)} className="border border-gray-300 rounded px-2 py-2"/></label>
            <label className="flex flex-col"><span className="text-[10px] uppercase tracking-widest text-gray-500 font-bold mb-1">Échéance</span><input type="date" value={((d.date_limite_auto !== false ? computeLimite(d.type, d.date_depot, d.zone_protegee, d.delai_mois_override) : (d.date_limite || "")) || "").slice(0, 10)} disabled={d.date_limite_auto !== false} onChange={(e) => set("date_limite", e.target.value)} data-testid="de-date-limite" className={`border border-gray-300 rounded px-2 py-2 ${d.date_limite_auto !== false ? "bg-gray-100 text-gray-500" : ""}`}/></label>
          </div>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2 bg-stone-50 border border-stone-100 rounded p-2.5">
            <label className="flex items-center gap-1.5 text-xs font-semibold text-gray-700">
              <input type="checkbox" checked={d.date_limite_auto !== false} onChange={(e) => set("date_limite_auto", e.target.checked)} data-testid="de-limite-auto" className="accent-[#C26B53]"/>
              Calcul auto de l'échéance (dépôt + délai)
            </label>
            {(d.type === "DP" || d.type === "ENSEIGNE" || d.type === "PC") && (
              <label className="flex items-center gap-1.5 text-xs font-semibold text-gray-700">
                <input type="checkbox" checked={!!d.zone_protegee} onChange={(e) => set("zone_protegee", e.target.checked)} data-testid="de-zone-protegee" className="accent-[#C26B53]"/>
                ABF (site protégé) {d.type === "PC" ? "→ +1 mois" : "→ 2 mois"}
              </label>
            )}
            <label className="flex items-center gap-1.5 text-xs font-semibold text-gray-700">
              Délai override
              <input type="number" min="0" placeholder="auto" value={d.delai_mois_override || ""} onChange={(e) => set("delai_mois_override", e.target.value ? parseInt(e.target.value) : null)} data-testid="de-delai-override" className="w-16 border border-gray-300 rounded px-2 py-1"/>
              <span className="text-gray-400 font-normal">mois</span>
            </label>
          </div>

          {/* ===== Lettre d'accompagnement (démarche à déposer) ===== */}
          <div className="border border-[#C26B53]/30 bg-[#C26B53]/5 rounded p-3 space-y-2" data-testid="de-lettre-section">
            <div className="flex items-center justify-between">
              <span className="text-[10px] uppercase tracking-widest text-[#C26B53] font-bold flex items-center gap-1.5"><Mail size={13}/> Lettre d'accompagnement</span>
              <button type="button" onClick={generateLettre} disabled={genLettre} data-testid="de-lettre-generate" className="flex items-center gap-1.5 text-xs font-semibold bg-[#C26B53] text-white px-3 py-1.5 rounded hover:opacity-90 disabled:opacity-50">
                {genLettre ? <Loader2 size={13} className="animate-spin"/> : <Sparkles size={13}/>} Générer (IA)
              </button>
            </div>
            <label className="flex flex-col"><span className="text-[10px] uppercase tracking-widest text-gray-500 font-bold mb-1">Mairie / service destinataire (nom + adresse)</span>
              <textarea value={d.mairie_destinataire || ""} onChange={(e) => set("mairie_destinataire", e.target.value)} rows={3} placeholder={"Mairie de …\nService urbanisme\n1 place …\n00000 Ville"} data-testid="de-mairie-dest" className="w-full border border-gray-300 rounded px-3 py-2 text-sm"/>
            </label>
            <label className="flex flex-col"><span className="text-[10px] uppercase tracking-widest text-gray-500 font-bold mb-1">Corps de la lettre (éditable)</span>
              <textarea value={d.lettre_texte || ""} onChange={(e) => set("lettre_texte", e.target.value)} rows={6} placeholder="Cliquez sur « Générer (IA) » puis ajustez le texte…" data-testid="de-lettre-texte" className="w-full border border-gray-300 rounded px-3 py-2 text-sm"/>
            </label>
            <button type="button" onClick={downloadLettrePdf} disabled={dlLettre || !(d.lettre_texte || "").trim()} data-testid="de-lettre-pdf" className="flex items-center gap-1.5 text-xs font-semibold border border-[#C26B53] text-[#C26B53] hover:bg-[#C26B53] hover:text-white px-3 py-1.5 rounded transition-colors disabled:opacity-40">
              {dlLettre ? <Loader2 size={13} className="animate-spin"/> : <FileDown size={13}/>} Télécharger la lettre PDF
            </button>
          </div>

          {/* Commentaire général */}
          <textarea value={d.commentaire || ""} onChange={(e) => set("commentaire", e.target.value)} placeholder="Commentaire général sur la démarche…" rows={2} data-testid="de-commentaire" className="w-full border border-gray-300 rounded px-3 py-2"/>

          {/* Suivi dossier mairie */}
          <div className="border-t border-stone-100 pt-3">
            <div className="text-[10px] uppercase tracking-widest text-gray-500 font-bold mb-1.5">Suivi dossier mairie</div>
            <select value={d.mairie_status || ""} onChange={(e) => set("mairie_status", e.target.value)} data-testid="de-mairie-status" className="w-full border border-gray-300 rounded px-3 py-2 mb-2">
              {MAIRIE_STATUS.map((m) => <option key={m.id} value={m.id}>{m.label}</option>)}
            </select>
            <textarea value={d.mairie_remarques || ""} onChange={(e) => set("mairie_remarques", e.target.value)} placeholder="Remarques (consultation ABF, motifs de refus, demandes de compléments…)" rows={3} data-testid="de-mairie-remarques" className="w-full border border-gray-300 rounded px-3 py-2"/>
          </div>

          <input value={d.instructeur_nom || ""} onChange={(e) => set("instructeur_nom", e.target.value)} placeholder="Instructeur" className="w-full border border-gray-300 rounded px-3 py-2"/>
          <div className="grid grid-cols-2 gap-2">
            <input value={d.instructeur_email || ""} onChange={(e) => set("instructeur_email", e.target.value)} placeholder="Email" className="border border-gray-300 rounded px-3 py-2"/>
            <input value={d.instructeur_tel || ""} onChange={(e) => set("instructeur_tel", e.target.value)} placeholder="Téléphone" className="border border-gray-300 rounded px-3 py-2"/>
          </div>

          {/* Plateforme & identifiants */}
          <div className="border-t border-stone-100 pt-3 space-y-2">
            <div className="text-[10px] uppercase tracking-widest text-gray-500 font-bold">Plateforme &amp; identifiants</div>
            <input value={d.plateforme || ""} onChange={(e) => set("plateforme", e.target.value)} placeholder="Plateforme numérique (URL)" data-testid="de-plateforme" className="w-full border border-gray-300 rounded px-3 py-2"/>
            <div className="grid grid-cols-2 gap-2">
              <input value={d.identifiant_mail || ""} onChange={(e) => set("identifiant_mail", e.target.value)} placeholder="Adresse mail" data-testid="de-id-mail" className="border border-gray-300 rounded px-3 py-2"/>
              <input value={d.identifiant_mdp || ""} onChange={(e) => set("identifiant_mdp", e.target.value)} placeholder="Mot de passe" data-testid="de-id-mdp" className="border border-gray-300 rounded px-3 py-2"/>
            </div>
          </div>

          {/* Interlocuteurs multiples */}
          <div className="border-t border-stone-100 pt-3">
            <div className="flex items-center justify-between mb-2">
              <div className="text-[10px] uppercase tracking-widest text-gray-500 font-bold">Interlocuteurs ({interlocs.length})</div>
              <button type="button" onClick={addInterloc} data-testid="de-add-interloc" className="text-xs font-semibold text-[#C26B53] hover:text-[#A6452F] flex items-center gap-1"><Plus size={13}/> Ajouter</button>
            </div>
            {interlocs.length === 0 ? (
              <div className="text-xs text-stone-400 italic">Aucun interlocuteur. Ajoutez la mairie, le bureau de contrôle, etc.</div>
            ) : (
              <div className="space-y-2">
                {interlocs.map((it) => (
                  <div key={it.id} className="grid grid-cols-12 gap-1.5 items-center" data-testid={`de-interloc-${it.id}`}>
                    <select value={it.role} onChange={(e) => setInterloc(it.id, "role", e.target.value)} className="col-span-3 border border-gray-300 rounded px-1.5 py-1.5 text-xs">
                      {INTERLOCUTEUR_ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
                    </select>
                    <input value={it.nom || ""} onChange={(e) => setInterloc(it.id, "nom", e.target.value)} placeholder="Nom" className="col-span-3 border border-gray-300 rounded px-2 py-1.5 text-xs"/>
                    <input value={it.email || ""} onChange={(e) => setInterloc(it.id, "email", e.target.value)} placeholder="Email" className="col-span-3 border border-gray-300 rounded px-2 py-1.5 text-xs"/>
                    <input value={it.tel || ""} onChange={(e) => setInterloc(it.id, "tel", e.target.value)} placeholder="Tél" className="col-span-2 border border-gray-300 rounded px-2 py-1.5 text-xs"/>
                    <button type="button" onClick={() => delInterloc(it.id)} className="col-span-1 text-stone-400 hover:text-rose-600 flex justify-center"><Trash2 size={13}/></button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Suivi du dossier (timeline) */}
          <div className="border-t border-stone-100 pt-3" data-testid="de-suivi">
            <div className="text-[10px] uppercase tracking-widest text-gray-500 font-bold mb-2">Suivi du dossier ({etapes.length})</div>
            {etapes.length > 0 && (
              <div className="space-y-1.5 mb-2">
                {etapes.slice().sort((a, b) => String(a.date).localeCompare(String(b.date))).map((e) => (
                  <div key={e.id} className="flex items-start gap-2 text-sm border-l-2 border-[#C26B53] pl-2 py-0.5" data-testid={`de-etape-${e.id}`}>
                    <span className="text-xs font-mono text-stone-500 shrink-0 w-20 pt-0.5">{formatDate(e.date)}</span>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-stone-800">{e.titre}</div>
                      {e.commentaire && <div className="text-xs text-stone-500">{e.commentaire}</div>}
                    </div>
                    <button type="button" onClick={() => delEtape(e.id)} className="text-stone-400 hover:text-rose-600 shrink-0"><Trash2 size={12}/></button>
                  </div>
                ))}
              </div>
            )}
            <div className="grid grid-cols-12 gap-1.5 items-center bg-stone-50 border border-stone-100 rounded p-2">
              <input type="date" value={newEtape.date} onChange={(e) => setNewEtape((p) => ({ ...p, date: e.target.value }))} data-testid="de-etape-date" className="col-span-3 border border-gray-300 rounded px-1.5 py-1.5 text-xs"/>
              <input value={newEtape.titre} onChange={(e) => setNewEtape((p) => ({ ...p, titre: e.target.value }))} placeholder="Titre (ex: Retour mairie)" data-testid="de-etape-titre" className="col-span-4 border border-gray-300 rounded px-2 py-1.5 text-xs"/>
              <input value={newEtape.commentaire} onChange={(e) => setNewEtape((p) => ({ ...p, commentaire: e.target.value }))} placeholder="Commentaire" className="col-span-4 border border-gray-300 rounded px-2 py-1.5 text-xs"/>
              <button type="button" onClick={addEtape} data-testid="de-etape-add" className="col-span-1 bg-[#C26B53] text-white rounded py-1.5 flex justify-center"><Plus size={14}/></button>
            </div>
          </div>

          {/* Documents */}
          <div className="border-t border-stone-100 pt-3">
            <div className="flex items-center justify-between mb-2">
              <div className="text-[10px] uppercase tracking-widest text-gray-500 font-bold">Documents ({docs.length})</div>
              <div className="flex items-center gap-1.5">
                <select value={kind} onChange={(e) => setKind(e.target.value)} data-testid="de-doc-kind" className="text-[11px] border border-gray-300 rounded px-1.5 py-1">
                  <option value="depose">Dossier déposé</option>
                  <option value="retour">Retour mairie</option>
                  <option value="autre">Autre</option>
                </select>
                <label className="text-xs font-semibold text-[#C26B53] hover:text-[#A6452F] cursor-pointer flex items-center gap-1" data-testid="de-doc-upload">
                  {uploading ? <Loader2 size={13} className="animate-spin"/> : <Upload size={13}/>} {uploading ? "Envoi…" : "Ajouter"}
                  <input type="file" disabled={uploading} className="hidden" onChange={(e) => { upload(e.target.files?.[0]); e.target.value = ""; }}/>
                </label>
              </div>
            </div>
            {docs.length === 0 ? (
              <div className="text-xs text-stone-400 italic">Aucun document. Joignez le dossier déposé et les retours mairie.</div>
            ) : (
              <div className="space-y-1">
                {docs.map((doc) => (
                  <div key={doc.id} className="flex items-center gap-2 py-1.5 px-1 hover:bg-stone-50 rounded text-sm" data-testid={`de-doc-${doc.id}`}>
                    <span className={`pill text-[9px] ${doc.kind === "retour" ? "bg-blue-50 text-blue-700" : doc.kind === "depose" ? "bg-emerald-50 text-emerald-700" : "bg-stone-100 text-stone-600"}`}>{KIND_LABEL[doc.kind] || doc.kind}</span>
                    <a href={`${API}/demarches/${demarche.id}/documents/${doc.id}`} target="_blank" rel="noreferrer" className="flex-1 font-semibold hover:text-[var(--ppd)] truncate">📄 {doc.filename}</a>
                    <button onClick={() => delDoc(doc.id)} data-testid={`de-doc-del-${doc.id}`} className="text-stone-400 hover:text-rose-600"><Trash2 size={13}/></button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        <div className="flex gap-2 mt-5 justify-end">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm border rounded">Fermer</button>
          <ActionButton onClick={save} data-testid="de-save" successLabel="Enregistré" className="px-4 py-2 text-sm bg-gray-900 text-white rounded font-semibold">Enregistrer</ActionButton>
        </div>
      </div>
    </div>
  );
}
